import { isTestingMode } from "../Controls/Testing";

import { LoadIntoDocument } from "./LoadIntoDocument";

declare global {
    interface Window {
        lastInputElement?: HTMLInputElement;
        kontur?: {
            drive?: {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                upload?: any;
                init: (callback: (error?: Error) => void) => void;
            };
        };
    }
}

export interface CustomFile {
    name: string;
    size: number;
    contentUri?: string;
    ndsTotalSum?: string | number;
}

export interface DriveTask {
    cancel: () => void;
    then: (resolve: (result: string | undefined) => void, reject: () => void) => undefined;
}

interface DriveResult {
    initDrive: Promise<void>;
    getDriveTask: () => DriveTask;
}

export type UploadToDrive = (file: CustomFile, changeProgress: (done: number, total: number) => void) => DriveResult;

let fileInput: HTMLInputElement | undefined;

export function selectUserFiles(multiple: boolean): Promise<CustomFile[]> {
    return new Promise(resolve => {
        const input = getFileInput();

        input.multiple = multiple;
        input.onchange = () =>
            onFileInputChange(input, files => {
                resolve(Array.prototype.slice.call(files, 0));
            });
        input.oncancel = () => {
            resolve([]);
        };
        if (isTestingMode()) {
            window.lastInputElement = input;
        } else {
            input.click();
        }
    });
}

export function uploadToDrive(file: CustomFile, onProgress: (done: number, total: number) => void): DriveResult {
    let initDrive = Promise.resolve();

    if (!window.kontur || !window.kontur.drive || !window.kontur.drive.upload) {
        initDrive = new Promise<void>((resolve, reject) => {
            // expected host: any.testkontur.ru or any.kontur.ru
            const host = window.location.hostname.split(".");

            host[0] = "api";
            const driveClientJsUri = `https://${host.join(".")}/drive/v1/js/client/js`;

            LoadIntoDocument.script(driveClientJsUri, error => {
                if (window.kontur == undefined || window.kontur.drive == undefined) {
                    reject(new Error("Drive client is not loaded"));
                    return;
                }
                if (error) {
                    reject(error);
                } else {
                    window.kontur.drive.init(err => {
                        if (err) {
                            reject(err);
                        } else {
                            resolve();
                        }
                    });
                }
            });
        });
    }

    return {
        initDrive: initDrive,
        getDriveTask: () => {
            if (window.kontur == undefined || window.kontur.drive == undefined) {
                throw new Error("Drive client is not loaded");
            }
            const driveTask = window.kontur.drive.upload(file, { daysToLive: 30, srv: "candyFront" });

            if (driveTask.progress) {
                driveTask.progress(onProgress);
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return driveTask;
        },
    };
}
function getFileInput(): HTMLInputElement {
    if (fileInput == undefined) {
        const input = document.createElement("input");

        input.type = "file";
        input.style.display = "none";

        // IE doesn't show open dialog if the input isn't in document.
        document.body.appendChild(input);
        fileInput = input;
    }

    return fileInput;
}

function onFileInputChange(input: HTMLInputElement, callback: (files: null | FileList) => void): void {
    if (fileInput != undefined && fileInput.parentNode != undefined) {
        fileInput.parentNode.removeChild(fileInput);
        fileInput.onchange = null;
        fileInput = undefined;
    }
    callback(input.files);
}

export function replaceExtension(fileName: string, newExt: string): string {
    const parts = fileName.split(".");
    if (parts.length > 1) {
        parts.pop(); // pop current extension
        return parts.concat(newExt).join(".");
    }
    return `${fileName}.${newExt}`;
}

export function replaceWithPdfExtension(fileName: string): string {
    return replaceExtension(fileName, "pdf");
}
